/**
 * Admin → Listings → one listing: the Eqaya Verification sign-off block.
 *
 * Shows what the verifier reported, how it differs from the realtor's dossier
 * (the computed flags) and the two decisions that close the check: mark the
 * listing verified — which publishes it — or fail it.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Link, Stack, TextField, Typography,
} from '@mui/material';
import { ErrorOutline, OpenInNew } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { panelSx, panelHeaderSx } from '../admin/surfaces';
import { text } from '../../theme/colors';

// The request status the admin cares about is "has the verifier been out yet",
// not the eight states the property-check engine moves through.
const REQUEST_CHIP = {
  pending: { label: 'Awaiting verifier', color: 'default' },
  assigned: { label: 'Verifier assigned', color: 'info' },
  accepted: { label: 'Verifier assigned', color: 'info' },
  in_progress: { label: 'Verifier assigned', color: 'info' },
  verification_completed: { label: 'Visit done', color: 'success' },
  approved: { label: 'Visit done', color: 'success' },
  completed: { label: 'Visit done', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};
const VISIT_DONE = ['verification_completed', 'approved', 'completed'];
const MIN_NOTE = 3;
const CONFIRM_COPY = 'This publishes the listing immediately.';

const asText = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export default function VerificationFindingsPanel({ listing, onMarkVerified, onFail, busy }) {
  const v = listing?.verification || null;
  const [fields, setFields] = useState([]);
  const [notes, setNotes] = useState({}); // flag key → dismissal note being typed
  const [confirm, setConfirm] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [failNotes, setFailNotes] = useState('');

  // Labels only — a template that fails to load costs readability, never the
  // decision, so the rows fall back to the raw field ids.
  useEffect(() => {
    let alive = true;
    adminService.listingVerificationFields()
      .then((f) => { if (alive) setFields(Array.isArray(f) ? f : []); })
      .catch(() => { if (alive) setFields([]); });
    return () => { alive = false; };
  }, []);

  // A re-read can dismiss or drop a flag under the admin; a note left behind
  // for a key that is no longer open would ride into the next sign-off.
  // findings/flags come straight out of a jsonb column: a hand-edited or
  // half-written row must cost a line of the panel, never the sign-off buttons.
  const flags = Array.isArray(v?.flags) ? v.flags.filter((f) => f && typeof f === 'object') : [];
  const findingsOk = !!v?.findings && typeof v.findings === 'object' && !Array.isArray(v.findings);
  const findings = findingsOk ? v.findings : null;
  const findingsBroken = !!v?.findings && !findingsOk;

  const openKeys = flags.filter((f) => f.severity === 'block' && !f.dismissedAt).map((f) => f.key).join('|');
  useEffect(() => {
    const open = new Set(openKeys ? openKeys.split('|') : []);
    setNotes((n) => {
      const kept = Object.keys(n).filter((k) => open.has(k));
      if (kept.length === Object.keys(n).length) return n;
      return Object.fromEntries(kept.map((k) => [k, n[k]]));
    });
  }, [openKeys]);

  if (!v) return null;

  const flagsBroken = v.flags !== undefined && v.flags !== null && !Array.isArray(v.flags);
  const chip = REQUEST_CHIP[v.status] || { label: v.status, color: 'default' };
  // The request status is the only word on whether the visit counts: a
  // cancelled request can still carry the timestamp of a visit that happened.
  const visitDone = VISIT_DONE.includes(v.status);
  const blockFlags = flags.filter((f) => f.severity === 'block');
  const noteFlags = flags.filter((f) => f.severity !== 'block');
  const openBlocks = blockFlags.filter((f) => !f.dismissedAt);
  const dismissals = openBlocks.map((f) => ({ key: f.key, note: (notes[f.key] || '').trim() }));
  const allDismissed = dismissals.every((d) => d.note.length >= MIN_NOTE);
  const canSignOff = !busy && visitDone && listing.status === 'verification_pending';

  // The template gives the order and the labels; anything the verifier sent
  // that the template does not know about is still shown, never swallowed.
  const known = fields.filter((f) => findings && Object.prototype.hasOwnProperty.call(findings, f.id));
  const extra = findings ? Object.keys(findings).filter((k) => !fields.some((f) => f.id === k)).map((k) => ({ id: k, label: k })) : [];
  const rows = [...known, ...extra];

  const runMarkVerified = () => { setConfirm(false); onMarkVerified(dismissals); };
  const runFail = () => { const n = failNotes.trim(); setFailOpen(false); setFailNotes(''); onFail(n); };

  return (
    <Box sx={{ ...panelSx, mb: 3 }}>
      <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>Eqaya Verification</Typography>
        <Chip size="small" color={chip.color} label={chip.label} />
        {v.reportUrl && (
          // Served by the API, not the SPA — a plain anchor so the browser
          // fetches the PDF with the admin's session cookies.
          <Link href={v.reportUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Report PDF <OpenInNew sx={{ fontSize: 14 }} />
          </Link>
        )}
      </Box>

      <Box sx={{ p: 2 }}>
        {!visitDone && <Alert severity="info" sx={{ mb: 2 }}>The verifier has not completed the visit yet.</Alert>}

        {(findingsBroken || flagsBroken) && (
          <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mb: 2 }}>
            Findings unavailable — this check&apos;s stored findings could not be read. Open the report PDF.
          </Typography>
        )}

        {rows.length > 0 && (
          <>
            <Typography variant="caption" sx={{ color: text.secondary, display: 'block', mb: 1 }}>Verifier findings</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {rows.map((f) => (
                <Grid item xs={12} sm={6} key={f.id}>
                  <Typography variant="caption" sx={{ color: text.secondary }}>{f.label || f.id}</Typography>
                  <Typography sx={{ fontWeight: 500 }}>{asText(findings[f.id])}</Typography>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {v.summary && (
          <>
            <Typography variant="caption" sx={{ color: text.secondary, display: 'block' }}>Verifier summary</Typography>
            <Typography sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{v.summary}</Typography>
          </>
        )}

        {flags.length === 0 && !flagsBroken && visitDone && <Alert severity="success" sx={{ mb: 2 }}>No discrepancies against the dossier.</Alert>}

        {blockFlags.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 1 }}>
              Discrepancies — each needs a written dismissal before sign-off
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {blockFlags.map((f) => (
                <Box key={f.key} sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 1, p: 1.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ErrorOutline fontSize="small" sx={{ color: 'error.main' }} />
                    <Typography sx={{ fontWeight: 600, color: 'error.main' }}>{f.label || f.key}</Typography>
                  </Stack>
                  {f.detail && <Typography variant="body2" sx={{ color: text.secondary, mb: 1 }}>{f.detail}</Typography>}
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    // Named, because two open flags mean two identical boxes.
                    label={f.dismissedAt ? `Dismissed: "${f.label || f.key}"` : `Why "${f.label || f.key}" is acceptable (required)`}
                    value={f.dismissedAt ? (f.dismissNote || '') : (notes[f.key] || '')}
                    // A dismissal is already on the record — showing it as an
                    // editable box would promise an edit that never lands.
                    InputProps={{ readOnly: !!f.dismissedAt }}
                    disabled={busy}
                    onChange={(e) => setNotes((n) => ({ ...n, [f.key]: e.target.value }))}
                  />
                </Box>
              ))}
            </Stack>
          </>
        )}

        {noteFlags.length > 0 && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {noteFlags.map((f) => (
              <Alert key={f.key} severity="warning">
                <strong>{f.label || f.key}</strong>{f.detail ? ` — ${f.detail}` : ''}
              </Alert>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" color="error" disabled={!canSignOff} onClick={() => { setFailNotes(''); setFailOpen(true); }}>Fail verification</Button>
          <Button variant="contained" color="success" disabled={!canSignOff || !allDismissed} onClick={() => setConfirm(true)}>Mark verified</Button>
        </Stack>
      </Box>

      <Dialog open={confirm} onClose={() => setConfirm(false)} fullWidth maxWidth="sm" aria-labelledby="mark-verified-title">
        <DialogTitle id="mark-verified-title">Mark this listing verified?</DialogTitle>
        <DialogContent>
          <Typography>{CONFIRM_COPY}</Typography>
          {dismissals.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1, color: text.secondary }}>
              {dismissals.length} discrepanc{dismissals.length === 1 ? 'y is' : 'ies are'} dismissed with your notes.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={busy} onClick={runMarkVerified}>Mark verified</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failOpen} onClose={() => setFailOpen(false)} fullWidth maxWidth="sm" aria-labelledby="fail-verification-title">
        <DialogTitle id="fail-verification-title">Fail this verification?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: text.secondary }}>The realtor is told why. The verification fee is not refunded.</Typography>
          <TextField fullWidth multiline minRows={3} label="Reason (required)" value={failNotes} onChange={(e) => setFailNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || failNotes.trim().length < MIN_NOTE} onClick={runFail}>Fail verification</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
